import {
  DatabaseAuto,
  IcommerceUser,
  Icustomer,
  Istaff,
  Isuccess,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

/**
 * Abstract base class that provides common functionality for user-related entities
 * in the commerce system. This class serves as a foundation for different types
 * of commerce users, handling shared properties and operations.
 *
 * @template T - The specific type of user data this class will handle
 * @extends {DatabaseAuto} Provides common database operation methods and properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link CommerceUserBase} class when invoking client operations.
 */
export abstract class CommerceUserBase extends DatabaseAuto {
  /**
   * The unique identifier of the company associated with this user.
   * @readonly
   */
  readonly company?: string;

  /**
   * The date when the user's association with the commerce system began.
   * @readonly
   */
  readonly startDate?: Date;

  /**
   * The date when the user's association with the commerce system ended or is scheduled to end.
   * @readonly
   */
  readonly endDate?: Date;

  /**
   * The occupation or role of the user within the commerce system.
   * @readonly
   */
  readonly occupation: string;

  /**
   * The core commerce user data containing authentication and profile information.
   * @readonly
   */
  readonly commerceUser: IcommerceUser;

  /**
   * Unique reference identifier for the user, used for external references.
   * @readonly
   */
  readonly urId: string;

  /**
   * Base URL for API endpoints related to this user type.
   * Should be overridden by derived classes to specify the appropriate endpoint.
   * @protected
   */
  protected baseUrl = '';

  /**
   * Creates a new instance of CommerceUserBase.
   *
   * @param {Object} data - The data used to initialize the instance.
   * @param {string} data._id - The unique identifier for the commerce user.
   * @param {Date} data.createdAt - The date when the user was created.
   * @param {Date} data.updatedAt - The date when the user was last updated.
   * @param {IcommerceUser} data.commerceUser - The commerce user's authentication and profile data.
   * @param {string} data.company - The unique identifier of the associated company.
   * @param {string} data.urId - The unique reference ID for the user.
   * @param {string} [data.startDate] - The start date in ISO string format (optional).
   * @param {string} [data.endDate] - The end date in ISO string format (optional).
   * @param {string} data.occupation - The occupation or role of the user.
   * @throws {Error} If required data is missing or invalid.
   * @throws {Error} If date validation fails for either startDate or endDate.
   *
   * @example
   * const userData = {
   *   _id: '123',
   *   createdAt: new Date(),
   *   updatedAt: new Date(),
   *   commerceUser: { /* ... *\/ },
   *   company: 'company-123',
   *   urId: 'user-ref-456',
   *   startDate: '2023-01-01T00:00:00.000Z',
   *   endDate: '2024-01-01T00:00:00.000Z',
   *   occupation: 'Manager'
   * };
   * const user = new ConcreteUser(userData);
   */
  constructor(data: {
    _id: string;
    createdAt: Date;
    updatedAt: Date;
    commerceUser: IcommerceUser;
    company: string;
    urId: string;
    startDate: string;
    endDate: string;
    occupation: string;
  } | Icustomer | Istaff) {
    // Validate input data
    validateData(data, {
      startDate: {
        required: false,
        validate: (v) => v === undefined || !isNaN(new Date(v).getTime()),
        message: 'Start date must be a valid date'
      },
      endDate: {
        required: false,
        validate: (v) => v === undefined || !isNaN(new Date(v).getTime()),
        message: 'End date must be a valid date'
      }
    });

    super(data);

    // Parse dates if provided
    if (data.startDate) {
      this.startDate = new Date(data.startDate);
    }
    if (data.endDate) {
      this.endDate = new Date(data.endDate);
    }

    // Assign properties
    this.occupation = data.occupation;
    this.commerceUser = data.commerceUser;
    this.company = data.company;
    this.urId = data.urId;
  }

  /**
   * Changes the default currency for the current user in the commerce system.
   * This operation updates the user's preferred currency setting and may affect
   * how prices and monetary values are displayed throughout the application.
   *
   * @param {string} currency - The ISO 4217 currency code to change to (e.g., 'USD', 'EUR', 'GBP').
   * @returns {Promise<Isuccess>} A promise that resolves to an object containing the operation status.
   * @throws {Error} If the currency change operation fails (e.g., invalid currency code, network error).
   * @throws {Error} If the user is not authenticated or lacks permission to change currency.
   *
   * @example
   * // Change the user's currency to US Dollars
   * await user.changeUserCurrency('USD')
   *   .then(result => console.log('Currency changed successfully:', result))
   *   .catch(error => console.error('Failed to change currency:', error));
   */
  changeUserCurrency(currency: string): Promise<Isuccess> {
    const observer$ = CommerceClient.ehttp
      .makePost<Isuccess>(
        `${this.baseUrl}/change-currency`,
        { currency }
      );

    return lastValueFrom(observer$);
  }
}
